import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TourStep } from '@/hooks/useProductTour';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface TourTooltipProps {
  step: TourStep | null;
  currentStepIndex: number;
  totalSteps: number;
  onSkip: () => void;
  onNext?: () => void;
  isFinalStep?: boolean;
}

interface TooltipPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

export function TourTooltip({ step, currentStepIndex, totalSteps, onSkip, onNext, isFinalStep }: TourTooltipProps) {
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const isMobile = useIsMobile();

  // Check if this step should be centered (no target selector or position is 'center')
  const shouldBeCentered = !step?.targetSelector || step?.position === 'center';

  const calculatePosition = useCallback(() => {
    if (!step || shouldBeCentered) {
      setPosition(null);
      return;
    }

    // Find the first VISIBLE element matching the selector (same logic as TourOverlay)
    const elements = document.querySelectorAll(step.targetSelector);
    let element: Element | null = null;
    
    for (const el of elements) {
      const style = window.getComputedStyle(el);
      const rect = el.getBoundingClientRect();
      // Check if element is visible AND has actual dimensions
      if (
        style.display !== 'none' && 
        style.visibility !== 'hidden' && 
        style.opacity !== '0' &&
        rect.width > 0 && 
        rect.height > 0
      ) {
        element = el;
        break;
      }
    }
    
    if (!element) {
      setPosition(null);
      return;
    }

    const rect = element.getBoundingClientRect();
    const tooltipWidth = isMobile ? window.innerWidth - 32 : 320;
    const tooltipHeight = 180;
    const offset = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Mobile: smart positioning based on target location
    if (isMobile) {
      const isNavStep = step.targetSelector.includes('nav-tabs');
      const isHistoryNoteStep = step.targetSelector.includes('history-note-button');
      const isBacklogStep = step.targetSelector.includes('backlog-button');
      const isTopElement = rect.top < viewportHeight / 2;
      
      if (isNavStep || isHistoryNoteStep) {
        // Nav / note button at bottom - position tooltip above it
        setPosition({
          bottom: 160,
          left: 16,
          arrowPosition: 'bottom',
        });
      } else if (isBacklogStep) {
        // Backlog button is in header - position tooltip well below target
        setPosition({
          top: rect.bottom + offset + 24,
          left: 16,
          arrowPosition: 'top',
        });
      } else if (isTopElement) {
        // Target is in top half - position tooltip BELOW the target
        setPosition({
          top: rect.bottom + offset + 8,
          left: 16,
          arrowPosition: 'top',
        });
      } else {
        // Target is in bottom half - position tooltip ABOVE the target
        setPosition({
          top: Math.max(16, rect.top - tooltipHeight - offset),
          left: 16,
          arrowPosition: 'bottom',
        });
      }
      return;
    }

    // Desktop: smart positioning based on available space
    let pos: TooltipPosition;

    switch (step.position) {
      case 'bottom':
        pos = {
          top: rect.bottom + offset,
          left: Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, viewportWidth - tooltipWidth - 16)),
          arrowPosition: 'top',
        };
        break;
      case 'top':
        pos = {
          top: rect.top - tooltipHeight - offset,
          left: Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, viewportWidth - tooltipWidth - 16)),
          arrowPosition: 'bottom',
        };
        break;
      case 'right':
        pos = {
          top: Math.max(16, Math.min(rect.top + rect.height / 2 - tooltipHeight / 2, viewportHeight - tooltipHeight - 16)),
          left: rect.right + offset,
          arrowPosition: 'left',
        };
        break;
      case 'left':
        pos = {
          top: Math.max(16, Math.min(rect.top + rect.height / 2 - tooltipHeight / 2, viewportHeight - tooltipHeight - 16)),
          left: rect.left - tooltipWidth - offset,
          arrowPosition: 'right',
        };
        break;
      default:
        pos = {
          top: rect.bottom + offset,
          left: Math.max(16, rect.left),
          arrowPosition: 'top',
        };
    }

    setPosition(pos);
  }, [step, isMobile, shouldBeCentered]);

  useEffect(() => {
    if (!step || shouldBeCentered) {
      setPosition(null);
      return;
    }

    // Initial calculation with delay for element to be in DOM
    const timer = setTimeout(calculatePosition, 100);

    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    const observer = new MutationObserver(calculatePosition);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
      observer.disconnect();
    };
  }, [step, calculatePosition, shouldBeCentered]);

  if (!step) return null;

  // If target element not found or step is centered, show centered floating tooltip
  const isCentered = shouldBeCentered || !position;

  const innerContent = (
    <>
      {/* Close button */}
      <button
        onClick={onSkip}
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </button>

      {/* Content */}
      <div className="space-y-3">
        <div>
          <h3 className="font-semibold text-foreground pr-6 flex items-center gap-2">
            {isFinalStep && <PartyPopper className="h-5 w-5 text-primary" />}
            {step.title}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            {step.message}
          </p>
        </div>

        {/* Progress dots and actions */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "w-2 h-2 rounded-full transition-colors",
                  i + 1 === currentStepIndex
                    ? "bg-primary"
                    : i + 1 < currentStepIndex
                      ? "bg-primary/50"
                      : "bg-muted-foreground/30"
                )}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {!isFinalStep && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onSkip}
                className="text-muted-foreground hover:text-foreground"
              >
                Skip tour
              </Button>
            )}
            {onNext && (
              <Button
                size="sm"
                onClick={onNext}
                className="gap-1"
              >
                {isFinalStep ? "Let's go!" : 'Next'}
                {!isFinalStep && <ChevronRight className="h-3 w-3" />}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const motionProps = {
    initial: { opacity: 0, y: 10, scale: 0.95 } as const,
    animate: { opacity: 1, y: 0, scale: 1 } as const,
    exit: { opacity: 0, y: 10, scale: 0.95 } as const,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  };

  const cardClasses = "p-4 rounded-xl bg-card/95 backdrop-blur-xl border border-border/50 shadow-2xl shadow-primary/10";

  const tooltipContent = isCentered ? (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center pointer-events-none px-4">
      <motion.div
        {...motionProps}
        className={cn(cardClasses, "pointer-events-auto w-full max-w-80 relative")}
      >
        {innerContent}
      </motion.div>
    </div>
  ) : (
    <AnimatePresence>
      <motion.div
        {...motionProps}
        className={cn(
          "fixed z-[99999]",
          cardClasses,
          isMobile ? "left-4 right-4" : "w-80"
        )}
        style={{
          top: position!.top,
          bottom: position!.bottom,
          left: isMobile ? 16 : position!.left,
          right: isMobile ? 16 : undefined,
        }}
      >
        {innerContent}

        {/* Arrow - desktop only */}
        {!isMobile && (
          <div
            className={cn(
              "absolute w-3 h-3 bg-card border-border/50 transform rotate-45",
              position!.arrowPosition === 'top' && "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 border-l border-t",
              position!.arrowPosition === 'bottom' && "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 border-r border-b",
              position!.arrowPosition === 'left' && "left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 border-l border-b",
              position!.arrowPosition === 'right' && "right-0 top-1/2 -translate-y-1/2 translate-x-1/2 border-r border-t"
            )}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(tooltipContent, document.body);
}
